import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import SportsEsportsRounded from '@mui/icons-material/SportsEsportsRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import profileImage from '../images/my_photo.webp';
import { getProjectBlurb } from '../projectsData';
import { CONTACT_EMAIL_ENDPOINT } from '../config';
import SkyRushLauncher from '../Components/SkyRushLauncher';
import { getSkillIconSvg } from './skillIcons';
import { NAV_TABS } from '../profileData';
import { useContent } from '../content/ContentProvider';
import { enableOwnerMode, isOwnerMode } from '../admin/ownerMode';
import sidebarDoodlesUrl from './assets/sidebar-doodles.svg';
import './site.css';

const HOME_HERO_IMAGE = '/images/home-lofi-coder.webp?v=2';

function OwnerLockButton({ className, onOpen }) {
  return (
    <button type="button" className={className} aria-label="Owner unlock" title="Owner unlock" onClick={onOpen}>
      <LockOutlined fontSize="small" />
    </button>
  );
}

function SkillIcon({ name }) {
  const icon = getSkillIconSvg(name);
  if (!icon || icon.fallback || !icon.path) {
    return <span className="site-skill-icon site-skill-icon--fallback">{name.slice(0, 1)}</span>;
  }
  const viewBox = icon.viewBox || '0 0 24 24';
  const rawHex = String(icon.hex || 'E39774').replace('#', '');
  const r = parseInt(rawHex.slice(0, 2), 16);
  const g = parseInt(rawHex.slice(2, 4), 16);
  const b = parseInt(rawHex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const fill = luminance < 0.28 ? '#e8dff7' : `#${rawHex}`;
  return (
    <svg
      className="site-skill-icon"
      role="img"
      viewBox={viewBox}
      aria-hidden="true"
    >
      <title>{icon.title}</title>
      <path d={icon.path} fill={fill} />
    </svg>
  );
}

function LanguageRing({ name, level }) {
  const ref = useRef(null);
  const [filled, setFilled] = useState(false);
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const offset = filled
    ? circumference - (level / 100) * circumference
    : circumference;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const root = el.closest('.site-sidebar');
    const io = new IntersectionObserver(
      ([entry]) => {
        setFilled(entry.isIntersecting && entry.intersectionRatio >= 0.4);
      },
      { root: root || null, threshold: [0, 0.4, 0.7, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className={`site-lang-ring${filled ? ' is-filled' : ''}`} ref={ref}>
      <svg viewBox="0 0 72 72" aria-hidden="true">
        <circle className="site-lang-ring__track" cx="36" cy="36" r={radius} />
        <circle
          className="site-lang-ring__progress"
          cx="36"
          cy="36"
          r={radius}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      <span className="site-lang-ring__value">{level}%</span>
      <span className="site-lang-ring__label">{name}</span>
    </div>
  );
}

function SidebarDoodles() {
  // Enough tiles to cover tall sidebars; CSS mask fades them out cleanly at the end.
  const tiles = Array.from({ length: 8 }, (_, i) => i);
  return (
    <div className="site-sidebar__doodles" aria-hidden="true">
      {tiles.map((i) => (
        <img
          key={i}
          className="site-sidebar__doodle-tile"
          src={sidebarDoodlesUrl}
          alt=""
          width={280}
          height={460}
          draggable={false}
        />
      ))}
    </div>
  );
}

function ProfileSidebar({ compact = false, showLock = false, onOpenLock }) {
  const { profile } = useContent();
  return (
    <aside className="site-sidebar" aria-label="Profile" aria-hidden={compact} inert={compact}>
      <div className="site-sidebar__stack">
        <SidebarDoodles />
        {showLock ? <OwnerLockButton className="site-lock" onOpen={onOpenLock} /> : null}
        <div className="site-sidebar__photo-wrap">
          <img className="site-sidebar__photo" src={profileImage} alt={profile.name} />
        </div>

        <div className="site-sidebar__identity">
          <h1 className="site-sidebar__name">{profile.name}</h1>
          <p className="site-sidebar__headline">{profile.headline}</p>
          <div className="site-status-pill" role="status">
            <span className="site-status-pill__dot" aria-hidden="true" />
            Open to 2026-2027 SWE/Full-Stack roles
          </div>
        </div>

        <div className="site-sidebar__residence">
          <span>Residence:</span>
          <strong>{profile.residence}</strong>
        </div>

        <div className="site-sidebar__tech">
          <h2 className="site-sidebar__block-title">Technical Proficiency</h2>
          {(profile.technicalProficiency || []).map((group) => (
            <React.Fragment key={group.name}>
              <div className="site-tech-group">
                <div className="site-tech-group__head">
                  <span>{group.name}</span>
                  <strong>{group.level}%</strong>
                </div>
                <div className="site-skill-bar__track" aria-hidden="true">
                  <div className="site-skill-bar__fill" style={{ width: `${group.level}%` }} />
                </div>
                <div className="site-tech-badges">
                  {(group.badges || group.items || []).map((item) => {
                    const label = String(item).split(',')[0].trim();
                    return (
                      <span className="site-tech-badge" key={`${group.name}-${label}`}>
                        <SkillIcon name={label} />
                        <em>{label}</em>
                      </span>
                    );
                  })}
                </div>
              </div>
              {group.name === 'Frontend & UI' ? (
                <aside className="site-tech-note" aria-label="Stack note">
                  <p>
                    Most of my work lives in the seam between UI and API — screens that stay calm,
                    contracts that stay honest, and deploy paths that don’t surprise anyone.
                  </p>
                </aside>
              ) : null}
            </React.Fragment>
          ))}
        </div>

        {(profile.certifications || []).length ? (
          <div className="site-sidebar__certs">
            <h2 className="site-sidebar__block-title">Certifications</h2>
            <ul className="site-cert-list">
              {profile.certifications.map((cert) => {
                const inner = (
                  <>
                    <strong>{cert.title}</strong>
                    <span>
                      {cert.issuer}
                      {cert.issued ? ` · ${cert.issued}` : ''}
                    </span>
                  </>
                );
                return (
                  <li key={`${cert.title}-${cert.issuer}`}>
                    {cert.url ? (
                      <a href={cert.url} target="_blank" rel="noopener noreferrer">
                        {inner}
                      </a>
                    ) : (
                      <div>{inner}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <div className="site-sidebar__languages">
          <h2 className="site-sidebar__block-title">Languages</h2>
          <div className="site-lang-rings" role="list">
            {profile.languages.map((lang) => (
              <div role="listitem" key={lang.name}>
                <LanguageRing name={lang.name} level={lang.level} />
              </div>
            ))}
          </div>
        </div>

        <div className="site-sidebar__socials">
          <a href={profile.links.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <LinkedInIcon fontSize="small" />
          </a>
          <a href={profile.links.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
            <GitHubIcon fontSize="small" />
          </a>
          <a href={`mailto:${profile.emails.primary}`} aria-label="Email">
            <EmailIcon fontSize="small" />
          </a>
        </div>
      </div>
    </aside>
  );
}

function PanelHead({ index, kicker, title, sub, action }) {
  return (
    <header className={`site-panel-head${action ? ' site-panel-head--with-action' : ''}`}>
      <p className="site-panel-head__kicker">
        <span className="site-panel-head__index">{index}</span>
        <span className="site-panel-head__slash" aria-hidden="true" />
        <span>{kicker}</span>
      </p>
      <div className="site-panel-head__row">
        <div className="site-panel-head__copy">
          <h2>{title}</h2>
          {sub ? <p className="site-panel-head__sub">{sub}</p> : null}
        </div>
        {action}
      </div>
    </header>
  );
}

function HomePanel({ onOpenTab, onOpenGame }) {
  const { profile, projects, downloadResume } = useContent();
  const reduceMotion = useReducedMotion();
  const featuredProjects = pickFeaturedProjects(projects);
  const focusLanes = [
    { label: 'Shipping', value: 'PLM · commerce · recipe platforms' },
    { label: 'Stack', value: 'Java · Python · React · Spring Boot' },
    { label: 'Next', value: 'Cloud systems · AI-assisted delivery' },
  ];
  const signalChips = ['Java', 'Python', 'React', 'Spring Boot', 'AWS', 'GenerativeAI'];
  const wallQuotes = [
    {
      text: 'Wear your failure as a badge of honor.',
      name: 'Sundar Pichai',
      role: 'CEO, Google',
      photo: '/images/quotes/sundar-pichai.png?v=13',
      mark: '/images/quotes/marks/google.svg?v=4',
      brand: 'google',
    },
    {
      text: 'Our industry does not respect tradition — it only respects innovation.',
      name: 'Satya Nadella',
      role: 'CEO, Microsoft',
      photo: '/images/quotes/satya-nadella.png?v=13',
      mark: '/images/quotes/marks/microsoft.svg?v=4',
      brand: 'microsoft',
    },
    {
      text: 'Talk is cheap. Show me the code.',
      name: 'Linus Torvalds',
      role: 'Creator of Linux',
      photo: '/images/quotes/linus-torvalds.png?v=13',
      mark: '/images/quotes/marks/linux.svg?v=4',
      brand: 'linux',
    },
    {
      text: 'Design is not just what it looks like and feels like. Design is how it works.',
      name: 'Steve Jobs',
      role: 'Co-founder, Apple',
      photo: '/images/quotes/steve-jobs.png?v=13',
      mark: '/images/quotes/marks/apple.svg?v=4',
      brand: 'apple',
    },
  ];
  const fieldNotes = [
    {
      kicker: 'Enterprise',
      title: 'PLM with real access control',
      detail: 'Keycloak RBAC, 66 APIs, and Cypress on Draft → Released — an operations desk, not a demo CRUD app.',
      href: 'https://github.com/anishneu/Enterprise-Asset-Management-System-PLM',
    },
    {
      kicker: 'Commerce',
      title: 'Wholesale platform, live',
      detail: 'Spring Boot 3 + React, JWT roles, and a store you can actually check out on Netlify + Render.',
      href: 'https://github.com/anishneu/MedicenceSupplies-Medical-Store-Platform',
    },
    {
      kicker: 'Playable',
      title: 'Sky Rush in this tab',
      detail: 'A Unity WebGL build hosted here. Most portfolios link out. This one lets you fly.',
      play: true,
    },
  ];
  const nowBoard = [
    {
      kicker: 'Shipping',
      title: 'E-commerce platforms in production',
      detail: 'Wholesale and storefront builds already live — Spring Boot, React, JWT roles, and real checkout paths.',
    },
    {
      kicker: 'Exploring',
      title: 'Gen AI · agentic workflows',
      detail: 'Digging into LLMs, agents, and AI-assisted delivery — the next tools I want on the desk.',
    },
    {
      kicker: 'Open',
      title: profile.status,
      detail: 'Boston desk. Willing to relocate to any city in the US.',
    },
  ];

  return (
    <section className="site-home">
      <div className="site-home__hero">
        <div
          className="site-home__hero-media"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HOME_HERO_IMAGE})` }}
        >
          <img
            src={HOME_HERO_IMAGE}
            alt=""
            fetchPriority="high"
            decoding="async"
          />
          <div className="site-home__hero-birds" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <span key={`bird-${n}`} className={`site-home__bird site-home__bird--${n}`}>
                <picture>
                  <source srcSet="/images/bird-flap.svg" type="image/svg+xml" />
                  <source srcSet="/images/bird-flap.webp" type="image/webp" />
                  <img src="/images/bird-flap.gif" alt="" draggable="false" />
                </picture>
              </span>
            ))}
          </div>
          <img
            className="site-home__hero-skyline"
            src="/images/home-hero-skyscraper-layer.png"
            alt=""
            aria-hidden="true"
            draggable="false"
          />
          <span className="site-home__hero-veil" />
          <span className="site-home__hero-scan" />
        </div>

        <div className="site-home__hero-copy">
          <h2 className="site-home__brand">{profile.name}</h2>
          <p className="site-home__role">Software Engineer · Full-Stack &amp; AI/ML</p>
          <p className="site-home__pitch">
            I design APIs and interfaces with care — clean under the hood, simple on the surface.
          </p>
          <div className="site-home__chip-row" aria-label="Core stack">
            {signalChips.map((chip, index) => (
              <motion.span
                key={chip}
                className="site-home__chip"
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 + index * 0.05, duration: 0.35 }}
              >
                {chip}
              </motion.span>
            ))}
          </div>
          <div className="site-home__banner-actions">
            <button type="button" className="site-btn site-btn--primary" onClick={downloadResume}>
              <DownloadRounded fontSize="small" /> Download resume
            </button>
            <button type="button" className="site-btn site-btn--ghost site-btn--on-dark" onClick={() => onOpenTab('about')}>
              About me
            </button>
          </div>
        </div>
      </div>

      <motion.div
        className="site-home__signal"
        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.45 }}
      >
        <div className="site-home__signal-head">
          <p className="site-home__eyebrow site-home__eyebrow--warm">Transmission</p>
          <h3>What I’m optimizing for right now</h3>
        </div>
        <div className="site-home__lanes">
          {focusLanes.map((lane, index) => (
            <motion.div
              className="site-home__lane"
              key={lane.label}
              initial={reduceMotion ? false : { opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.4 }}
            >
              <span>{lane.label}</span>
              <strong>{lane.value}</strong>
            </motion.div>
          ))}
        </div>
        <button type="button" className="site-home__signal-cta" onClick={() => onOpenTab('projects')}>
          Explore builds <ArrowForwardRounded fontSize="small" />
        </button>
      </motion.div>

      <div className="site-home__featured">
        <div className="site-home__featured-head">
          <div>
            <p className="site-home__eyebrow">Featured work</p>
            <h3>Selected projects</h3>
          </div>
          <button type="button" className="site-btn site-btn--ghost" onClick={() => onOpenTab('projects')}>
            View projects
          </button>
        </div>
        <div className="site-home__featured-grid" role="list">
          {featuredProjects.map((project, index) => {
            const subjects = projectCardSubjects(project);
            return (
              <motion.button
                type="button"
                className="site-home__card"
                role="listitem"
                key={project.id}
                onClick={() => onOpenTab('projects', project.id)}
                aria-label={`View ${project.title}`}
                initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{
                  delay: index * 0.08,
                  duration: 0.5,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <span className="site-home__card-beam" aria-hidden="true" />
                <div className="site-home__card-media">
                  <img
                    src={project.image}
                    alt=""
                    loading="lazy"
                    style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
                  />
                  {project.comingSoon ? (
                    <span className="site-home__card-soon">Coming soon</span>
                  ) : null}
                </div>
                <div className="site-home__card-body">
                  <p className="site-home__card-meta">
                    {subjects.length ? `${project.year} · ${subjects.join(' · ')}` : project.year}
                  </p>
                  <h4>{project.title}</h4>
                  <p>{project.summary}</p>
                  <div className="site-tags">
                    {projectCardTags(project).map((tag) => (
                      <span className="site-tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="site-home__quotes">
        <div className="site-home__featured-head">
          <div>
            <p className="site-home__eyebrow site-home__eyebrow--warm">On the wall</p>
            <h3>Lines I keep nearby</h3>
          </div>
        </div>
        <div className="site-home__quote-grid">
          {wallQuotes.map((quote, index) => (
            <motion.figure
              className={`site-home__quote site-home__quote--${quote.brand}`}
              key={quote.name}
              initial={reduceMotion ? false : { opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ delay: index * 0.06, duration: 0.4 }}
            >
              <span
                className="site-home__quote-mark"
                style={{ WebkitMaskImage: `url(${quote.mark})`, maskImage: `url(${quote.mark})` }}
                aria-hidden="true"
              />
              <div className="site-home__quote-photo">
                <img
                  src={quote.photo}
                  alt=""
                  width={72}
                  height={72}
                  decoding="async"
                  loading="lazy"
                />
              </div>
              <div className="site-home__quote-body">
                <blockquote>“{quote.text}”</blockquote>
                <figcaption>
                  <cite>{quote.name}</cite>
                  <span>{quote.role}</span>
                </figcaption>
              </div>
            </motion.figure>
          ))}
        </div>
      </div>

      <div className="site-home__notes">
        <div className="site-home__featured-head">
          <div>
            <p className="site-home__eyebrow">In the field</p>
            <h3>What this site can prove that a resume line can’t</h3>
          </div>
        </div>
        <div className="site-home__log" role="list">
          {fieldNotes.map((note, index) => {
            const body = (
              <>
                <span className="site-home__log-index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="site-home__log-copy">
                  <p className="site-home__eyebrow">{note.kicker}</p>
                  <h4>{note.title}</h4>
                  <p>{note.detail}</p>
                </div>
                <span className="site-home__log-arrow" aria-hidden="true">
                  <ArrowForwardRounded fontSize="small" />
                </span>
              </>
            );
            return note.play ? (
              <button
                type="button"
                className="site-home__log-row"
                role="listitem"
                key={note.title}
                onClick={onOpenGame}
              >
                {body}
              </button>
            ) : (
              <motion.a
                className="site-home__log-row"
                role="listitem"
                key={note.title}
                href={note.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: index * 0.06, duration: 0.35 }}
              >
                {body}
              </motion.a>
            );
          })}
        </div>
      </div>

      <div className="site-home__now">
        <div className="site-home__featured-head">
          <div>
            <p className="site-home__eyebrow site-home__eyebrow--warm">Now</p>
            <h3>What the desk looks like tonight</h3>
          </div>
          <button type="button" className="site-btn site-btn--ghost" onClick={() => onOpenTab('contact')}>
            Write to me
          </button>
        </div>
        <div className="site-home__now-grid">
          {nowBoard.map((item, index) => {
            const inner = (
              <>
                <p className="site-home__eyebrow site-home__eyebrow--warm">{item.kicker}</p>
                <h4>{item.title}</h4>
                <p>{item.detail}</p>
              </>
            );
            return item.play ? (
              <button
                type="button"
                className="site-home__now-card"
                key={item.title}
                onClick={onOpenGame}
              >
                {inner}
                <span className="site-home__now-link">
                  Play Sky Rush <SportsEsportsRounded fontSize="inherit" />
                </span>
              </button>
            ) : (
              <motion.article
                className="site-home__now-card"
                key={item.title}
                initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ delay: index * 0.06, duration: 0.4 }}
              >
                {inner}
              </motion.article>
            );
          })}
        </div>
      </div>

      <footer className="site-home__copyright">
        <p>© {new Date().getFullYear()} Anish Kuila. All rights reserved.</p>
      </footer>
    </section>
  );
}

function AboutPanel() {
  const { profile, education } = useContent();
  return (
    <section>
      <PanelHead
        index="02"
        kicker="About"
        title="About me"
        sub="A bit about me, the work I do, and how I got here."
      />
      {profile.about.map((para) => (
        <p className="site-lead" key={para.slice(0, 24)}>
          {para}
        </p>
      ))}
      <dl className="site-about-facts">
        <div>
          <dt>Location</dt>
          <dd>{profile.location}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span className="site-status-pill site-status-pill--inline">
              <span className="site-status-pill__dot" aria-hidden="true" />
              {profile.status}
            </span>
          </dd>
        </div>
        <div>
          <dt>Citizenship</dt>
          <dd>{profile.citizenship}</dd>
        </div>
      </dl>
      <header className="site-panel-head site-panel-head--nested">
        <h3>Education</h3>
        <p className="site-panel-head__sub">Degrees and coursework.</p>
      </header>
      <ol className="site-edu-tree">
        {education.map((edu) => (
          <li className="site-edu-tree__branch" key={edu.school}>
            <article className="site-edu-tree__node">
              <div className="site-edu-tree__info">
                <p className="site-edu-tree__when">{edu.dates}</p>
                <h3>{edu.school}</h3>
                <h4>{edu.degree}</h4>
                <p className="site-edu-tree__place">{edu.location}</p>
              </div>
              <ul className="site-edu-tree__leaves">
                {(edu.courses || []).map((course) => (
                  <li key={course}>{course}</li>
                ))}
              </ul>
            </article>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ExperiencePanel() {
  const { experience } = useContent();
  const reduceMotion = useReducedMotion();
  return (
    <section className="site-experience">
      <PanelHead
        index="04"
        kicker="Experience"
        title="Work experience"
        sub="Field deployments — research, shipping, and what moved the needle."
      />
      <div className="site-exp-rail">
        {experience.map((job, index) => (
          <motion.article
            className="site-exp-card"
            key={`${job.company}-${job.dates}`}
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.45, delay: index * 0.06 }}
          >
            <div className="site-exp-card__spine" aria-hidden="true">
              <span className="site-exp-card__node" />
            </div>
            <div className="site-exp-card__panel">
              <div className="site-exp-card__top">
                <p className="site-exp-card__index">Mission {String(index + 1).padStart(2, '0')}</p>
                <p className="site-exp-card__dates">{job.dates}</p>
              </div>
              <h3>{job.title}</h3>
              <div className="site-exp-card__meta">
                <span className="site-exp-card__company">{job.company}</span>
                {job.location ? <span className="site-exp-card__place">{job.location}</span> : null}
              </div>
              <ol className="site-exp-card__bullets">
                {(job.bullets || []).map((bullet, bulletIndex) => (
                  <li key={bullet}>
                    <em>{String(bulletIndex + 1).padStart(2, '0')}</em>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ol>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

const CATEGORY_LABELS = {
  'full-stack': 'Full-stack',
  games: 'Games',
  ml: 'AI / ML',
  design: 'Design',
  healthcare: 'Healthcare',
};

/** Home + Featured filter order: Lugensa → PLM → Medicence → Face Detection */
const FEATURED_ORDER = [8, 7, 2, 4];

function pickFeaturedProjects(projects) {
  return FEATURED_ORDER.map((id) => projects.find((project) => String(project.id) === String(id))).filter(
    Boolean
  );
}

function projectCardTags(project) {
  const tags = project?.highlightTags?.length ? project.highlightTags : project?.tags || [];
  return tags.slice(0, 3);
}

function projectCardSubjects(project) {
  if (Array.isArray(project?.subjects) && project.subjects.length) {
    return project.subjects.slice(0, 3);
  }
  const category = CATEGORY_LABELS[project?.category] || project?.category;
  return category ? [category] : [];
}

function ProjectDetailModal({ project, onClose }) {
  useEffect(() => {
    if (!project) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [project, onClose]);

  if (!project) return null;

  return (
    <div
      className="site-project-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-title"
      onClick={onClose}
    >
      <div
        className="site-project-modal__panel"
        onClick={(e) => e.stopPropagation()}
        style={{ '--project-hue': project.spectrum?.hue ?? 24 }}
      >
        <button
          type="button"
          className="site-project-modal__close"
          aria-label="Close project details"
          onClick={onClose}
        >
          <CloseRounded fontSize="small" />
        </button>
        <div className="site-project-modal__layout">
          <div className="site-project-modal__col site-project-modal__col--media">
            <div className="site-project-modal__visual">
              <img
                src={project.image}
                alt=""
                style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
              />
            </div>
            <div className="site-project-modal__aside">
              <div className="site-project-modal__visual-meta site-project-modal__visual-meta--inline">
                <span>{CATEGORY_LABELS[project.category] || project.category}</span>
                {project.featured || FEATURED_ORDER.some((id) => String(id) === String(project.id)) ? (
                  <em>Featured</em>
                ) : null}
                {project.comingSoon ? <em className="is-soon">Coming soon</em> : null}
              </div>
              {project.metrics?.length ? (
                <div className="site-project-modal__metrics">
                  {project.metrics.map((metric) => (
                    <div key={`${project.id}-m-${metric.label}`}>
                      <strong>{metric.value}</strong>
                      <span>{metric.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {getProjectBlurb(project).length ? (
                <div className="site-project-modal__brief">
                  {getProjectBlurb(project).map((line) => (
                    <p key={line.slice(0, 40)}>{line}</p>
                  ))}
                </div>
              ) : null}
              <div className="site-project-modal__stack">
                <h4>Highlights</h4>
                <div className="site-tags">
                  {projectCardTags(project).map((tag) => (
                    <span className="site-tag" key={`hi-${tag}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="site-project-modal__dossier">
            <p className="site-project-modal__kicker">
              {project.year}
              {project.role ? ` · ${project.role}` : ''}
              {project.spectrum?.band ? ` · ${project.spectrum.band}` : ''}
            </p>
            <h3 id="project-modal-title">{project.title}</h3>
            <p className="site-project-modal__lead">{project.description || project.summary}</p>
            {project.highlights?.length ? (
              <div className="site-project-modal__block">
                <h4>What I shipped</h4>
                <ol>
                  {project.highlights.map((item, index) => (
                    <li key={item}>
                      <em>{String(index + 1).padStart(2, '0')}</em>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            <div className="site-project-modal__stack">
              <h4>Stack</h4>
              <div className="site-tags">
                {project.tags.map((tag) => (
                  <span className="site-tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            {(project.liveUrl && project.liveUrl !== '#') ||
            (project.sourceUrl && project.sourceUrl !== '#') ? (
              <div className="site-project-modal__links">
                {project.liveUrl && project.liveUrl !== '#' ? (
                  project.liveUrl.startsWith('/') ? (
                    <RouterLink className="site-btn site-btn--ghost" to={project.liveUrl}>
                      Live / Play
                    </RouterLink>
                  ) : (
                    <a
                      className="site-btn site-btn--ghost"
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Live demo <OpenInNewRounded fontSize="inherit" />
                    </a>
                  )
                ) : null}
                {project.sourceUrl && project.sourceUrl !== '#' ? (
                  <a
                    className="site-btn site-btn--ghost"
                    href={project.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub <OpenInNewRounded fontSize="inherit" />
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const PROJECT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'featured', label: 'Featured' },
  { id: 'full-stack', label: 'Full-stack' },
  { id: 'games', label: 'Games' },
  { id: 'ml', label: 'AI / ML' },
  { id: 'design', label: 'Design' },
  { id: 'healthcare', label: 'Healthcare' },
];

function ProjectsPanel({ initialProjectId = null, onCloseProject }) {
  const { projects } = useContent();
  const [activeId, setActiveId] = useState(initialProjectId);
  const [filter, setFilter] = useState('all');
  const active = projects.find((project) => String(project.id) === String(activeId)) || null;

  useEffect(() => {
    if (initialProjectId == null || initialProjectId === '') return;
    setActiveId(initialProjectId);
  }, [initialProjectId]);

  const closeProject = useCallback(() => {
    setActiveId(null);
    onCloseProject?.();
  }, [onCloseProject]);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) => {
        const orderA = Number(a.order ?? Number.MAX_SAFE_INTEGER);
        const orderB = Number(b.order ?? Number.MAX_SAFE_INTEGER);
        if (orderA !== orderB) return orderA - orderB;
        const yearDelta = Number(b.year) - Number(a.year);
        if (yearDelta !== 0) return yearDelta;
        return String(a.title || '').localeCompare(String(b.title || ''));
      }),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    if (filter === 'featured') return pickFeaturedProjects(projects);
    if (filter === 'all') return sortedProjects;
    return sortedProjects.filter((project) => project.category === filter);
  }, [filter, projects, sortedProjects]);

  const filterCounts = useMemo(() => {
    const counts = { all: projects.length, featured: pickFeaturedProjects(projects).length };
    projects.forEach((project) => {
      counts[project.category] = (counts[project.category] || 0) + 1;
    });
    return counts;
  }, [projects]);

  return (
    <section className="site-projects">
      <PanelHead
        index="05"
        kicker="Projects"
        title="Projects"
        sub="Selected builds across product, games, and research."
      />

      <div className="site-project-filters" role="tablist" aria-label="Filter projects">
        {PROJECT_FILTERS.filter((item) => item.id === 'all' || item.id === 'featured' || filterCounts[item.id]).map(
          (item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`site-project-filter${filter === item.id ? ' is-active' : ''}`}
              aria-selected={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          )
        )}
      </div>

      {filteredProjects.length === 0 ? (
        <p className="site-project-empty">No projects in this filter yet.</p>
      ) : (
        <div className="site-project-grid">
          {filteredProjects.map((project) => (
            <button
              type="button"
              className="site-project-tile"
              key={project.id}
              onClick={() => setActiveId(project.id)}
            >
              <div className="site-project-tile__media">
                <img
                  src={project.image}
                  alt=""
                  loading="lazy"
                  style={project.imagePosition ? { objectPosition: project.imagePosition } : undefined}
                />
                {project.comingSoon ? (
                  <span className="site-project-tile__soon">Coming soon</span>
                ) : null}
                <span className="site-project-tile__hover">
                  <span className="site-project-tile__hover-meta">
                    {project.year}
                    {FEATURED_ORDER.some((id) => String(id) === String(project.id)) ? ' · Featured' : ''}
                    {project.comingSoon ? ' · Coming soon' : ''}
                  </span>
                  <span className="site-project-tile__hover-cta">
                    View project <ArrowForwardRounded fontSize="inherit" />
                  </span>
                </span>
              </div>
              <h3>{project.title}</h3>
              <p>{CATEGORY_LABELS[project.category] || project.category}</p>
            </button>
          ))}
        </div>
      )}

      <ProjectDetailModal project={active} onClose={closeProject} />
    </section>
  );
}

function SkillsPanel() {
  const { skillGroups } = useContent();
  const [activeGroup, setActiveGroup] = useState(skillGroups[0]?.title || '');
  const current = skillGroups.find((group) => group.title === activeGroup) || skillGroups[0];

  useEffect(() => {
    if (!skillGroups.some((group) => group.title === activeGroup)) {
      setActiveGroup(skillGroups[0]?.title || '');
    }
  }, [skillGroups, activeGroup]);

  return (
    <section className="site-skills">
      <PanelHead
        index="03"
        kicker="Skills"
        title="Skills"
        sub="Pick a lane to inspect the stack."
      />

      <div className="site-skills__board">
        <div className="site-skills__rail" role="tablist" aria-label="Skill categories">
          {skillGroups.map((group, index) => (
            <button
              key={group.title}
              type="button"
              role="tab"
              className={`site-skills__rail-btn${activeGroup === group.title ? ' is-active' : ''}`}
              aria-selected={activeGroup === group.title}
              onClick={() => setActiveGroup(group.title)}
            >
              <em>{String(index + 1).padStart(2, '0')}</em>
              <span>{group.title}</span>
              <strong>{group.items.length}</strong>
            </button>
          ))}
        </div>

        <div className="site-skills__stage" role="tabpanel">
          <div className="site-skills__orbit" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="site-skills__stage-head">
            <p className="site-home__eyebrow">Lane {String(skillGroups.findIndex((g) => g.title === current?.title) + 1).padStart(2, '0')}</p>
            <h3>{current?.title}</h3>
          </div>
          <div className="site-skills__constellation">
            {(current?.items || []).map((item, index) => (
              <div
                className="site-skills__node"
                key={item}
                style={{ '--i': index }}
              >
                <span className="site-skills__node-icon">
                  <SkillIcon name={item} />
                </span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ResourcesPanel() {
  return (
    <section className="site-resources">
      <PanelHead
        index="06"
        kicker="Resources"
        title="Resources"
        sub="Notes, guides, and downloads I am gathering here."
      />
      <div className="site-resources__soon" role="status">
        <p className="site-resources__soon-kicker">Status</p>
        <h3>Coming Soon</h3>
        <p>
          This section is under construction. Check back for write-ups, templates, and other
          useful material.
        </p>
      </div>
    </section>
  );
}

function ContactPanel() {
  const { profile, downloadResume } = useContent();
  const [form, setForm] = useState({ fullName: '', email: '', message: '' });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);

  const onChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: 'err', text: 'Please fill in name, email, and message.' });
      return;
    }
    setSending(true);
    setStatus({ type: '', text: '' });
    try {
      const res = await fetch(CONTACT_EMAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderName: form.fullName.trim(),
          senderEmail: form.email.trim(),
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error('send_failed');
      setForm({ fullName: '', email: '', message: '' });
      setStatus({ type: 'ok', text: 'Message sent. Thanks for reaching out!' });
    } catch {
      setStatus({
        type: 'err',
        text: `Could not send via API. Email ${profile.emails.primary} directly.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="site-contact">
      <PanelHead
        index="07"
        kicker="Contact"
        title="Open a channel"
        sub="Open to software, full-stack, and AI roles."
        action={(
          <button type="button" className="site-btn site-btn--ghost" onClick={downloadResume}>
            <DownloadRounded fontSize="small" /> Resume
          </button>
        )}
      />

      <div className="site-channel">
        <aside className="site-channel__board">
          <div className="site-channel__radar" aria-hidden="true">
            <span />
            <span />
            <span />
            <i />
          </div>
          <p className="site-channel__live">
            <span className="site-channel__pulse" aria-hidden="true" />
            Line is open
          </p>
          <h3>Direct frequencies</h3>
          <p className="site-channel__note">
            Prefer email for recruiting notes. I usually reply within a day.
          </p>
          <ul className="site-channel__freqs">
            <li>
              <a href={`mailto:${profile.emails.primary}`}>
                <EmailIcon fontSize="small" />
                <span>
                  <strong>Primary</strong>
                  <em>{profile.emails.primary}</em>
                </span>
              </a>
            </li>
            <li>
              <a href={`mailto:${profile.emails.school}`}>
                <EmailIcon fontSize="small" />
                <span>
                  <strong>School</strong>
                  <em>{profile.emails.school}</em>
                </span>
              </a>
            </li>
            <li>
              <div>
                <PhoneIcon fontSize="small" />
                <span>
                  <strong>Phone</strong>
                  <em>{profile.phone}</em>
                </span>
              </div>
            </li>
            <li>
              <div>
                <PlaceIcon fontSize="small" />
                <span>
                  <strong>Location</strong>
                  <em>{profile.location}</em>
                </span>
              </div>
            </li>
            <li>
              <a href={profile.links.linkedin} target="_blank" rel="noopener noreferrer">
                <LinkedInIcon fontSize="small" />
                <span>
                  <strong>LinkedIn</strong>
                  <em>Connect</em>
                </span>
              </a>
            </li>
            <li>
              <a href={profile.links.github} target="_blank" rel="noopener noreferrer">
                <GitHubIcon fontSize="small" />
                <span>
                  <strong>GitHub</strong>
                  <em>Code</em>
                </span>
              </a>
            </li>
          </ul>
        </aside>

        <div className="site-channel__compose">
          <div className="site-channel__compose-bar">
            <span>New transmission</span>
            <span>SMTP · Boston</span>
          </div>
          <form className="site-contact-form" onSubmit={onSubmit}>
            <div className="site-contact-form__row">
              <div className="site-field">
                <label htmlFor="fullName">
                  <PersonOutlineRounded fontSize="inherit" /> Full name
                </label>
                <input id="fullName" value={form.fullName} onChange={onChange('fullName')} autoComplete="name" />
              </div>
              <div className="site-field">
                <label htmlFor="email">
                  <EmailIcon fontSize="inherit" /> Email
                </label>
                <input id="email" type="email" value={form.email} onChange={onChange('email')} autoComplete="email" />
              </div>
            </div>
            <div className="site-field">
              <label htmlFor="message">
                <SendRounded fontSize="inherit" /> Message
              </label>
              <textarea id="message" rows={6} value={form.message} onChange={onChange('message')} />
            </div>
            <div className="site-contact-form__actions">
              <button type="submit" className="site-btn site-btn--primary" disabled={sending}>
                <SendRounded fontSize="small" />
                {sending ? 'Sending…' : 'Transmit'}
              </button>
            </div>
            {status.text ? (
              <p className={`site-toast site-toast--${status.type === 'ok' ? 'ok' : 'err'}`}>{status.text}</p>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}

function randomBit() {
  return Math.random() > 0.5 ? '1' : '0';
}

function makeStream(width, height, depth) {
  const font = depth === 'near'
    ? 16 + Math.round(Math.random() * 6)
    : depth === 'mid'
      ? 11 + Math.round(Math.random() * 3)
      : 8 + Math.round(Math.random() * 2);
  const line = font * 1.18;
  const maxRows = Math.min(34, Math.ceil(height / line) + 3);
  const rows = Math.max(10, Math.floor(maxRows * (0.55 + Math.random() * 0.45)));
  return {
    x: Math.random() * width,
    y: Math.random() < 0.6 ? Math.random() * height * 0.1 : Math.random() * height * 0.5,
    font,
    line,
    depth,
    warm: Math.random() < 0.16,
    opacity: depth === 'near' ? 0.86 : depth === 'mid' ? 0.44 : 0.2,
    bits: Array.from({ length: rows }, randomBit),
    head: Math.floor(Math.random() * rows),
    pace: depth === 'near' ? 90 + Math.random() * 70 : 120 + Math.random() * 90,
    last: 0,
  };
}

function BinaryRain({ active }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let streams = [];
    let frame = 0;
    let running = true;
    let lastPaint = 0;

    const layout = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = Math.max(1, width);
      canvas.height = Math.max(1, height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const density = width < 700 ? 0.75 : 1;
      streams = [
        ...Array.from({ length: Math.round(28 * density) }, () => makeStream(width, height, 'far')),
        ...Array.from({ length: Math.round(16 * density) }, () => makeStream(width, height, 'mid')),
        ...Array.from({ length: Math.round(8 * density) }, () => makeStream(width, height, 'near')),
      ];
    };

    const paint = (now) => {
      if (!running) return;
      if (now - lastPaint < 33) {
        frame = window.requestAnimationFrame(paint);
        return;
      }
      lastPaint = now;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      ctx.clearRect(0, 0, width, height);
      streams.forEach((stream) => {
        if (!reduce && now - stream.last >= stream.pace) {
          stream.last = now;
          stream.head = (stream.head + 1) % stream.bits.length;
          stream.bits[stream.head] = randomBit();
        }
        ctx.font = `600 ${stream.font}px "IBM Plex Mono", ui-monospace, monospace`;
        stream.bits.forEach((bit, index) => {
          const y = stream.y + index * stream.line;
          if (y < -stream.line || y > height) return;
          const fromHead = (index - stream.head + stream.bits.length) % stream.bits.length;
          const glow = fromHead === 0 ? 1 : Math.max(0.22, 1 - fromHead * 0.11);
          const alpha = stream.opacity * glow;
          if (fromHead === 0 && stream.depth === 'near') {
            ctx.fillStyle = stream.warm ? 'rgba(243, 214, 198, 0.95)' : 'rgba(244, 236, 255, 0.96)';
          } else if (stream.warm) {
            ctx.fillStyle = `rgba(227, 151, 116, ${alpha})`;
          } else {
            ctx.fillStyle = `rgba(183, 148, 246, ${alpha})`;
          }
          ctx.fillText(bit, stream.x, y);
        });
      });
      frame = window.requestAnimationFrame(paint);
    };

    layout();
    if (active) frame = window.requestAnimationFrame(paint);
    return () => {
      running = false;
      window.cancelAnimationFrame(frame);
    };
  }, [active]);

  return <canvas className="site-boot__rain" ref={canvasRef} aria-hidden="true" />;
}

function BootSplash({ onDone }) {
  const { profile } = useContent();
  const [phase, setPhase] = useState('load'); // load -> strike -> split -> done
  const [progress, setProgress] = useState(0);
  const letters = profile.name.split('');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setProgress(100);
      setPhase('done');
      onDoneRef.current();
      return undefined;
    }

    const start = performance.now();
    const loadMs = 1700;
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / loadMs);
      const eased = 1 - (1 - t) ** 2;
      setProgress(Math.round(eased * 100));
      if (t < 1) {
        frame = window.requestAnimationFrame(tick);
      } else {
        setProgress(100);
      }
    };
    frame = window.requestAnimationFrame(tick);

    const timers = [
      window.setTimeout(() => setPhase('strike'), 1760),
      window.setTimeout(() => setPhase('split'), 2100),
      window.setTimeout(() => {
        setPhase('done');
        onDoneRef.current();
      }, 2920),
    ];

    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  return (
    <div
      className={`site-boot is-${phase}`}
      aria-live="polite"
      aria-busy={phase !== 'done'}
    >
      <div className="site-boot__door site-boot__door--left" aria-hidden="true" />
      <div className="site-boot__door site-boot__door--right" aria-hidden="true" />
      <BinaryRain active={phase === 'load' || phase === 'strike'} />
      <div className="site-boot__frame" aria-hidden="true">
        <i /><i /><i /><i />
      </div>
      <div className="site-boot__seam" aria-hidden="true" />
      <div className="site-boot__flash" aria-hidden="true" />

      <svg
        className="site-boot__bolt"
        viewBox="0 0 80 400"
        preserveAspectRatio="xMidYMin meet"
        aria-hidden="true"
      >
        <polyline
          className="site-boot__bolt-glow"
          points="40,0 48,42 32,42 46,98 28,98 44,160 30,160 42,230 34,230 40,310 36,310 40,400"
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polyline
          className="site-boot__bolt-path"
          points="40,0 48,42 32,42 46,98 28,98 44,160 30,160 42,230 34,230 40,310 36,310 40,400"
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polyline
          className="site-boot__bolt-branch"
          points="46,98 62,118 54,118"
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polyline
          className="site-boot__bolt-branch"
          points="30,160 14,184 22,184"
          fill="none"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>

      <div className="site-boot__inner">
        <h1 className="site-boot__name">
          {letters.map((ch, index) => (
            <span
              key={`${ch}-${index}`}
              className="site-boot__letter"
              style={{ '--i': index }}
            >
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </h1>
        <p className="site-boot__tag">Software Engineer · Full-Stack & AI/ML</p>
        <div className="site-boot__meter" aria-hidden={phase === 'done'}>
          <div className="site-boot__track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <div className="site-boot__fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="site-boot__percent">{progress}%</span>
        </div>
      </div>
    </div>
  );
}

function HiringTicker({ active, onDismiss, onTurnsDone }) {
  const { profile } = useContent();
  useEffect(() => {
    if (!active) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) return undefined;
    const id = window.setTimeout(onTurnsDone, 8000);
    return () => window.clearTimeout(id);
  }, [active, onTurnsDone]);

  if (!active) return null;
  const line = profile.ticker;
  const onSlideEnd = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== 'site-ticker-slide') return;
    onTurnsDone();
  };

  return (
    <div className="site-ticker" role="status">
      <div className="site-ticker__viewport">
        <div className="site-ticker__rail" onAnimationEnd={onSlideEnd}>
          <span>{line}</span>
          <span>{line}</span>
        </div>
      </div>
      <button type="button" className="site-ticker__close" onClick={onDismiss} aria-label="Dismiss notice">
        <CloseRounded fontSize="inherit" />
      </button>
    </div>
  );
}

export default function SiteShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useContent();
  const [booting, setBooting] = useState(true);
  const [gameOpen, setGameOpen] = useState(false);
  const [tab, setTab] = useState(() => location.state?.openTab || 'home');
  const [openProjectId, setOpenProjectId] = useState(() => location.state?.openProjectId ?? null);
  const [glider, setGlider] = useState({ x: 0, w: 0 });
  const [tickerOn, setTickerOn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ownerMode, setOwnerMode] = useState(() => isOwnerMode());
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 980px)').matches : false
  );
  const railRef = useRef(null);
  const tickerWaitRef = useRef(0);
  const panelScrollRef = useRef(null);
  const finishBoot = useCallback(() => setBooting(false), []);

  const openOwnerGate = useCallback(() => {
    enableOwnerMode();
    setOwnerMode(true);
    navigate('/admin/otp');
  }, [navigate]);

  useEffect(() => {
    const img = new Image();
    img.src = HOME_HERO_IMAGE;
    img.decode?.().catch(() => {});
  }, []);

  useEffect(() => {
    if (isOwnerMode()) setOwnerMode(true);
    const onKey = (event) => {
      if (event.ctrlKey && event.altKey && event.code === 'KeyL') {
        event.preventDefault();
        openOwnerGate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search, openOwnerGate]);

  const hideTicker = useCallback(() => {
    setTickerOn(false);
    window.clearTimeout(tickerWaitRef.current);
    tickerWaitRef.current = window.setTimeout(() => setTickerOn(true), 90 * 1000);
  }, []);

  const updateGlider = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const active = rail.querySelector('.site-tab.is-active');
    if (!active) return;
    setGlider({ x: active.offsetLeft, w: active.offsetWidth });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 980px)');
    const sync = () => {
      setIsCompact(mq.matches);
      if (!mq.matches) setMenuOpen(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    const html = document.documentElement;
    const previousHtml = html.style.overflow;
    const previousBody = document.body.style.overflow;
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      html.style.overflow = previousHtml;
      document.body.style.overflow = previousBody;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const openTab = useCallback((id, projectId = null) => {
    setTab(id);
    setMenuOpen(false);
    setOpenProjectId(id === 'projects' ? projectId : null);
  }, []);

  useEffect(() => {
    if (location.state?.openTab) {
      setTab(location.state.openTab);
      setOpenProjectId(
        location.state.openTab === 'projects' ? location.state.openProjectId ?? null : null
      );
      window.history.replaceState({}, '');
    }
    if (location.state?.openGame) {
      setGameOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    updateGlider();
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateGlider);
    observer.observe(rail);
    window.addEventListener('resize', updateGlider);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateGlider);
    };
  }, [tab, updateGlider]);

  useEffect(() => {
    if (booting) return undefined;
    const firstId = window.setTimeout(() => setTickerOn(true), 900);
    return () => {
      window.clearTimeout(firstId);
      window.clearTimeout(tickerWaitRef.current);
    };
  }, [booting]);

  useEffect(() => {
    const node = panelScrollRef.current;
    if (!node) return;
    node.scrollTop = 0;
    const chrome = document.querySelector('.site-chrome');
    const chromeHeight = chrome?.offsetHeight || 0;
    const top = node.getBoundingClientRect().top + window.scrollY - chromeHeight;
    window.scrollTo({ top: Math.max(0, top) });
  }, [tab]);

  const clearOpenProject = useCallback(() => setOpenProjectId(null), []);

  const panel = useMemo(() => {
    switch (tab) {
      case 'about':
        return <AboutPanel />;
      case 'skills':
        return <SkillsPanel />;
      case 'experience':
        return <ExperiencePanel />;
      case 'education':
        return <AboutPanel />;
      case 'projects':
        return (
          <ProjectsPanel
            initialProjectId={openProjectId}
            onCloseProject={clearOpenProject}
          />
        );
      case 'resources':
        return <ResourcesPanel />;
      case 'contact':
        return <ContactPanel />;
      default:
        return null;
    }
  }, [tab, openProjectId, clearOpenProject]);

  return (
    <>
      {booting ? <BootSplash onDone={finishBoot} /> : null}
      <div className="site-shell">
        <ProfileSidebar compact={isCompact} showLock={ownerMode} onOpenLock={openOwnerGate} />
        <div className="site-main">
          <div className="site-chrome">
          <header className="site-mobile-bar" aria-hidden={!isCompact}>
            <div className="site-mobile-bar__lead">
              {ownerMode ? <OwnerLockButton className="site-lock site-lock--bar" onOpen={openOwnerGate} /> : null}
              <button
                type="button"
                className="site-mobile-bar__brand"
                onClick={() => openTab('home')}
              >
                {profile.name}
              </button>
            </div>
            <button
              type="button"
              className="site-mobile-bar__menu"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="site-mobile-menu"
              onClick={() => setMenuOpen(true)}
            >
              <MenuRounded />
            </button>
          </header>
          <nav className="site-tabs" aria-label="Primary" aria-hidden={isCompact}>
            <div className="site-tabs__inner">
              <div className="site-tabs__track" ref={railRef} role="tablist">
                <span
                  className="site-tabs__glider"
                  style={{ width: glider.w, transform: `translateX(${glider.x}px)` }}
                  aria-hidden="true"
                />
                {NAV_TABS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    className={`site-tab${tab === item.id ? ' is-active' : ''}`}
                    onClick={() => openTab(item.id)}
                    aria-selected={tab === item.id}
                    aria-current={tab === item.id ? 'page' : undefined}
                    tabIndex={isCompact ? -1 : undefined}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="site-game-btn"
                aria-label="Play Sky Rush"
                title="Play Sky Rush"
                onClick={() => setGameOpen(true)}
                tabIndex={isCompact ? -1 : undefined}
              >
                <SportsEsportsRounded fontSize="small" />
                <span>Play</span>
              </button>
            </div>
          </nav>
          <HiringTicker active={tickerOn} onDismiss={hideTicker} onTurnsDone={hideTicker} />
          </div>
          <div className="site-panel" ref={panelScrollRef}>
            <img
              className="site-home-hero-cache"
              src={HOME_HERO_IMAGE}
              alt=""
              fetchPriority="high"
              decoding="async"
              aria-hidden="true"
            />
            <div
              className={`site-panel__inner${tab === 'home' ? '' : ' is-parked'}`}
              aria-hidden={tab !== 'home'}
              inert={tab !== 'home'}
            >
              <HomePanel onOpenTab={openTab} onOpenGame={() => setGameOpen(true)} />
            </div>
            {tab !== 'home' ? (
              <div
                className="site-panel__inner"
                key={tab === 'projects' ? `projects-${openProjectId ?? 'list'}` : tab}
              >
                {panel}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {menuOpen ? (
        <div className="site-mobile-drawer" role="dialog" aria-modal="true" aria-labelledby="site-mobile-menu-title">
          <button
            type="button"
            className="site-mobile-drawer__scrim"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          <div className="site-mobile-drawer__panel" id="site-mobile-menu">
            <div className="site-mobile-drawer__head">
              <p id="site-mobile-menu-title">Menu</p>
              <button
                type="button"
                className="site-mobile-drawer__close"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              >
                <CloseRounded />
              </button>
            </div>
            <nav className="site-mobile-drawer__nav" aria-label="Pages">
              {NAV_TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`site-mobile-drawer__link${tab === item.id ? ' is-active' : ''}`}
                  onClick={() => openTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <button
              type="button"
              className="site-btn site-btn--primary site-mobile-drawer__play"
              onClick={() => {
                setMenuOpen(false);
                setGameOpen(true);
              }}
            >
              <SportsEsportsRounded fontSize="small" /> Play Sky Rush
            </button>
            <div className="site-mobile-drawer__socials">
              <a href={profile.links.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <LinkedInIcon fontSize="small" />
              </a>
              <a href={profile.links.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <GitHubIcon fontSize="small" />
              </a>
              <a href={`mailto:${profile.emails.primary}`} aria-label="Email">
                <EmailIcon fontSize="small" />
              </a>
            </div>
          </div>
        </div>
      ) : null}
      <SkyRushLauncher hideFab open={gameOpen} onOpenChange={setGameOpen} />
    </>
  );
}
